import type { ParsedDocument, TableCellValue } from '../app/types'

export type TableSortDirection = 'asc' | 'desc'

export interface IndexedTableRow {
  row: TableCellValue[]
  index: number
}

export function sortTableRows<T extends IndexedTableRow>(
  rows: T[],
  columnIndex: number,
  direction: TableSortDirection,
): T[] {
  const multiplier = direction === 'asc' ? 1 : -1
  return [...rows].sort((left, right) => {
    const leftValue = left.row[columnIndex]
    const rightValue = right.row[columnIndex]
    const leftEmpty = leftValue === null || leftValue === ''
    const rightEmpty = rightValue === null || rightValue === ''

    if (leftEmpty || rightEmpty) {
      return leftEmpty === rightEmpty ? 0 : leftEmpty ? 1 : -1
    }

    return compareTableCellValues(leftValue, rightValue) * multiplier
  })
}

export function compareTableCellValues(left: TableCellValue, right: TableCellValue): number {
  const leftEmpty = left === null || left === ''
  const rightEmpty = right === null || right === ''

  if (leftEmpty || rightEmpty) {
    return leftEmpty === rightEmpty ? 0 : leftEmpty ? 1 : -1
  }

  const leftNumber = toComparableNumber(left)
  const rightNumber = toComparableNumber(right)
  if (leftNumber !== undefined && rightNumber !== undefined) {
    return leftNumber - rightNumber
  }

  return String(left).localeCompare(String(right), 'zh-CN', {
    numeric: true,
    sensitivity: 'base',
  })
}

function toComparableNumber(value: TableCellValue): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return undefined
  }

  const number = Number(value.replaceAll(',', '').trim())
  return Number.isFinite(number) ? number : undefined
}

export function updateTableCell(
  document: ParsedDocument,
  sheetIndex: number,
  rowIndex: number,
  colIndex: number,
  value: string,
): ParsedDocument {
  const sheets = document.sheets?.map((sheet, index) => {
    if (index !== sheetIndex) {
      return sheet
    }

    const rows = sheet.rows.map((row, currentRow) => {
      if (currentRow !== rowIndex) {
        return row
      }

      return row.map((cell, currentCol) => (currentCol === colIndex ? value : cell))
    })

    return { ...sheet, rows }
  })

  return {
    ...document,
    sheets,
    textContent: sheets
      ?.map((sheet) => `${sheet.name}\n${sheet.headers.join(' ')}\n${sheet.rows.map((row) => row.join(' ')).join('\n')}`)
      .join('\n\n') ?? document.textContent,
  }
}

export function switchSheet(document: ParsedDocument, sheetIndex: number): ParsedDocument {
  return {
    ...document,
    activeSheetIndex: sheetIndex,
  }
}
